import { API_ORIGIN } from '../config';
const API_BASE = `${API_ORIGIN}`;

const dynamicFormsAdminService = {
  // Get all forms
  getAllForms: async (isActive = null) => {
    const queryParam = isActive !== null ? `?is_active=${isActive ? 1 : 0}` : '';
    const response = await fetch(`${API_BASE}/api/forms${queryParam}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch forms');
    }
    
    return response.json();
  },

  // Get specific form with fields
  getForm: async (formId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch form');
    }
    
    return response.json();
  },

  // Get form with layout (for editing)
  getFormWithLayout: async (formId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/layout`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch form with layout');
    }
    
    return response.json();
  },

  // Get form fields
  getFields: async (formId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/fields`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch form fields');
    }
    
    return response.json();
  },

  // Get submissions for a form
  getSubmissions: async (formId, limit = 50, offset = 0) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/submissions?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch submissions');
    }
    
    return response.json();
  },

  // Get specific submission details
  getSubmission: async (submissionId) => {
    const response = await fetch(`${API_BASE}/api/forms/submissions/${submissionId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch submission');
    }
    
    return response.json();
  },

  // Export submissions
  exportSubmissions: async (formId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/submissions/export`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to export submissions');
    }
    
    return response.json();
  },

  // All submissions as the backend's CSV (question labels as headers)
  exportSubmissionsCsv: async (formId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/submissions/export?format=csv`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to export submissions');
    }

    return { success: true, csvContent: await response.text() };
  },

  // Send the confirmation, review and internal emails for a submission again
  resendSubmissionEmails: async (submissionId) => {
    const response = await fetch(`${API_BASE}/api/forms/submissions/${submissionId}/send-all-emails`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to send emails');
    }

    return data;
  },

  // Export submissions grouped by workshop
  exportSubmissionsGroupedByWorkshop: async (formId, workshopFieldName) => {
    const response = await fetch(
      `${API_BASE}/api/forms/${formId}/submissions/export?format=csv&group_by_workshop=${encodeURIComponent(workshopFieldName)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to export submissions');
    }
    
    // Return the text content directly for CSV
    const csvContent = await response.text();
    return { success: true, csvContent };
  },

  // Get form statistics
  getFormStats: async (formId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/stats`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch form stats');
    }
    
    return response.json();
  },

  // Get form by client username
  getFormByClientUsername: async (username) => {
    const response = await fetch(`${API_BASE}/api/forms/client/${encodeURIComponent(username)}/form`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    // 404 is expected when client doesn't have a dynamic form
    if (response.status === 404) {
      const data = await response.json();
      throw new Error(data.error || 'No dynamic form assigned');
    }
    
    if (!response.ok) {
      throw new Error('Failed to fetch client form');
    }
    
    return response.json();
  },

  // Update form (name, active status, payment link)
  updateForm: async (formId, updateData) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updateData),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update form');
    }
    
    return response.json();
  },

  // Toggle form active status
  toggleFormActive: async (formId, isActive) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_active: isActive }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to toggle form status');
    }
    
    return response.json();
  },

  // Toggle payment enabled
  togglePayment: async (formId, paymentEnabled) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/toggle-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ payment_enabled: paymentEnabled }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to toggle payment');
    }
    
    return response.json();
  },

  // Get email configuration for a form
  getEmailConfig: async (formId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/email-config`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch email configuration');
    }
    
    return response.json();
  },

  // Create or update email configuration
  updateEmailConfig: async (formId, config) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/email-config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(config),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update email configuration');
    }
    
    return response.json();
  },

  // Delete email configuration
  deleteEmailConfig: async (formId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/email-config`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete email configuration');
    }
    
    return response.json();
  },

  // Delete submission
  deleteSubmission: async (formId, submissionId) => {
    const response = await fetch(`${API_BASE}/api/forms/${formId}/submission/${submissionId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete submission');
    }
    
    return response.json();
  },

  // Download all attachments for a submission as ZIP
  downloadAllAttachments: async (submissionId) => {
    const response = await fetch(`${API_BASE}/api/forms/submissions/${submissionId}/download-all`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to download attachments');
    }
    
    // Return blob for ZIP file
    const blob = await response.blob();
    return blob;
  },
};

export default dynamicFormsAdminService;
